'use strict'

// SFCC Hooks
import {useMemo} from 'react'
import {useProductSearch} from '@salesforce/commerce-sdk-react'

// Utils
import {
    isProductEligibleForAutomaticLinking,
    getAutomaticLinkingRefinements
} from '../utils/recommendation-utils'

/* HOW TO USE THIS HOOK

import {useAutomaticLinking} from '../../hooks/use-automatic-linking'
const {automaticLinkingResult, isAutomaticLinkingEnabled, isAutomaticLinkingLoading} = useAutomaticLinking(product)

*/

const automaticLinkDisabledData = {
    automaticLinkingResult: null,
    isAutomaticLinkingLoading: false,
    isAutomaticLinkingEnabled: false
}

/**
 * Custom React hook to handle automatic product linking based on eligibility and product search results.
 *
 * Determines if a given product is eligible for automatic linking, performs a product search with
 * specific refinements, and returns the available products that can be automatically linked.
 * If automatic linking is not enabled or no available products are found, returns default disabled data.
 *
 * @param {Object} product - The product object to check for automatic linking eligibility.
 * @returns {Object} An object containing:
 *   - {Array} automaticLinkingResult: List of available products for automatic linking.
 *   - {boolean} isAutomaticLinkingLoading: Indicates if the product search is loading.
 *   - {boolean} isAutomaticLinkingEnabled: Indicates if automatic linking is enabled for the product.
 *   If automatic linking is not enabled or no products are available, returns fallback disabled data.
 */
export const useAutomaticLinking = (product) => {
    let isAutomaticLinkingEnabled = false

    // Check if the product is eligible for automatic linking
    if (isProductEligibleForAutomaticLinking(product)) {
        isAutomaticLinkingEnabled = true
    }

    const {data: productSearchResult, isLoading} = useProductSearch(
        {
            parameters: {
                refine: getAutomaticLinkingRefinements(product) || [],
                allVariationProperties: true,
                expand: ['availability', 'images', 'prices', 'promotions', 'variations'],
                // attribute to check SFS availability of the search hits
                c_sfsAvailability: true
            }
        },
        {
            enabled: isAutomaticLinkingEnabled // Only when there is no manual linking
        }
    )

    const availableProducts = useMemo(() => {
        return (
            productSearchResult?.hits?.filter((hit) => hit.inventory?.orderable || hit.orderable) ||
            []
        )
    }, [productSearchResult])

    if (availableProducts.length === 0 || !isAutomaticLinkingEnabled) {
        return automaticLinkDisabledData
    }

    return {
        automaticLinkingResult: availableProducts,
        isAutomaticLinkingLoading: isLoading,
        isAutomaticLinkingEnabled: isAutomaticLinkingEnabled
    }
}
